import ApiSmile from '@/api/api-smile';
import ActionArea from '@/component/action-area';
import {ActionButton} from '@/component/action-area/action-area';
import DataRow from '@/component/data-row';
import PageContainer from '@/component/page-container';
import LoadingUtil from '@/util/loading-util';
import MessageUtil from '@/util/message-util';
import Utils from '@/util/utils';
import {DatePicker, Form, InputNumber, Modal, Radio, Select} from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import moment, {Moment} from 'moment';
import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router';
import {HoldData} from '../types';
import './hold-sell.scss';

interface HoldSellData {
    quantity: number;
    sellTime: Moment;
    sellPrice: number;
    sellDescription: string;
    id: number;
}

interface HoldSellable {
    code: string;
    name: string;
    quantity: number;
}

const HoldSell = () => {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [holdSellable, setHoldSellable] = useState<HoldSellable[]>([]);
    const [selectedSell, setSelectedSell] = useState<HoldSellable>();
    const [holdBought, setHoldBought] = useState<HoldData[]>([]);

    useEffect(() => {
        form.setFieldsValue({
            sellTime: moment(),
            sellPrice: 0,
            quantity: 0,
            sellDescription: '',
            buyOrder: 0,
        });
    }, [form]);

    useEffect(() => {
        const load = async () => {
            LoadingUtil.show();
            const response: any = await ApiSmile.getHoldSellable();
            if (response?.data?.code === 0) {
                setHoldSellable(response.data.data || []);
            } else {
                const msg = response?.data?.msg || '网络请求失败';
                MessageUtil.error(msg);
            }
            LoadingUtil.hide();
        };

        load();
    }, []);

    useEffect(() => {
        const load = async () => {
            LoadingUtil.show();
            const response: any = await ApiSmile.getHoldBought(selectedSell?.code || '');
            if (response?.data?.code === 0) {
                setHoldBought(response.data.data || []);
            } else {
                const msg = response?.data?.msg || '网络请求失败';
                MessageUtil.error(msg);
            }
            LoadingUtil.hide();
        };

        if (selectedSell) {
            load();
        }
    }, [selectedSell]);

    useEffect(() => {
        if (holdBought.length > 0) {
            form.setFieldsValue({
                buyOrder: holdBought[0].id,
            });
            form.setFieldsValue({quantity: holdBought[0].quantity});
        }
    }, [holdBought, form]);

    const onCancelClick = () => {
        navigate('/hold', {replace: true});
    };

    const validateData = (payload: HoldSellData, sell?: HoldSellable) => {
        if (!(sell?.code && sell?.name)) {
            MessageUtil.error('请选择名称和代码');
            return false;
        }
        if (!payload.sellTime) {
            MessageUtil.error('卖出时间不能为空');
            return false;
        }
        if (!Number.isFinite(payload.sellPrice)) {
            MessageUtil.error('卖出价格格式错误');
            return false;
        }
        if (payload.sellPrice <= 0) {
            MessageUtil.error('卖出价格不能小于0');
            return false;
        }
        if (!Number.isFinite(payload.quantity)) {
            MessageUtil.error('卖出数量格式错误');
            return false;
        }
        if (payload.quantity <= 0) {
            MessageUtil.error('卖出数量不能小于0');
            return false;
        }
        const find = holdBought.find((item) => item.id === payload.id);
        if (!find) {
            MessageUtil.error('请选择买入的订单');
            return false;
        }
        if (payload.quantity > find.quantity) {
            MessageUtil.error('卖出数量不能大于持有数量');
            return false;
        }

        return true;
    };

    const onSaveClick = () => {
        const value = form.getFieldsValue() || {};
        const payload: HoldSellData = {
            quantity: Number(value.quantity) || 0,
            sellTime: value.sellTime,
            sellPrice: Number(value.sellPrice) || 0,
            sellDescription: value.sellDescription || '',
            id: Number(value.buyOrder) || 0,
        };
        const sell = holdSellable.find((item) => item.code === (value.code || ''));

        if (!validateData(payload, sell)) {
            return;
        }

        const create = async () => {
            LoadingUtil.show();
            const response: any = await ApiSmile.createHoldSell(sell?.code || '', payload);
            if (response?.data?.code === 0) {
                navigate(`/hold`, {replace: true});
                MessageUtil.info('保存成功');
            } else {
                const msg = response?.data?.msg || '网络请求失败';
                MessageUtil.error(msg);
            }
            LoadingUtil.hide();
        };

        Modal.confirm({
            title: '确定卖出吗？',
            content: (
                <div>
                    <div>{`代码：${sell?.code || ''}`}</div>
                    <div>{`名称：${sell?.name || ''}`}</div>
                    <div>{`数量：${payload.quantity}`}</div>
                    <div>{`时间：${payload.sellTime.format('YYYY-MM-DD')}`}</div>
                    <div>{`价格：${payload.sellPrice}`}</div>
                    <div>{`描述：${payload.sellDescription}`}</div>
                </div>
            ),
            onOk: create,
        });
    };

    const getButtons = () => {
        const buttons: ActionButton[] = [];
        buttons.push({text: '取消', onClick: onCancelClick});
        buttons.push({text: '保存', onClick: onSaveClick, type: 'primary'});
        return buttons;
    };

    const onCodeSelectChange = (value: any) => {
        const find = holdSellable.find((item) => item.code === value);
        setSelectedSell(find);
    };

    const onBuyOrderSelect = (value: any) => {
        const find = holdBought.find((item) => item.id === value);
        form.setFieldsValue({quantity: find?.quantity || 0});
    };

    return (
        <PageContainer className="page-hold-sell">
            <ActionArea buttons={getButtons()} />
            <Form form={form}>
                <DataRow title="名称代码">
                    <Form.Item name={'code'}>
                        <Select showSearch optionFilterProp="children" onChange={onCodeSelectChange}>
                            {holdSellable.map((item) => (
                                <Select.Option key={item.code} value={item.code}>
                                    {`${item.name}(${item.code})`}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </DataRow>
                <DataRow title="卖出时间">
                    <Form.Item name={'sellTime'}>
                        <DatePicker />
                    </Form.Item>
                </DataRow>
                <DataRow title="卖出价格">
                    <Form.Item name={'sellPrice'}>
                        <InputNumber className="data-desc price-input" stringMode step={'0.01'} min={0} />
                    </Form.Item>
                </DataRow>
                <DataRow title="买入订单">
                    <Form.Item name={'buyOrder'}>
                        <Radio.Group onChange={(e) => onBuyOrderSelect(e.target.value)}>
                            {holdBought.map((item) => (
                                <Radio.Button key={item.id} value={item.id} className="mr-2 mb-1 h-auto px-2">
                                    <div className="w-36 py-1 text-black">
                                        <div className={'flex'}>
                                            <span className="flex-1 font-semibold text-lg">{Utils.formatPrice(item.buyPrice)}</span>
                                            <span className="flex-1">
                                                {'×'}
                                                <span className="font-black text-base">{item.quantity}</span>
                                            </span>
                                        </div>
                                        <div className="truncate text-sm">{item.buyDescription}</div>
                                        <div className="text-xs text-right">{Utils.formatDate(item.buyTime)}</div>
                                    </div>
                                </Radio.Button>
                            ))}
                        </Radio.Group>
                    </Form.Item>
                </DataRow>
                <DataRow title="卖出数量">
                    <Form.Item name={'quantity'}>
                        <InputNumber className="data-desc price-input" stringMode step={'10'} min={0} max={selectedSell?.quantity || 10000} />
                    </Form.Item>
                </DataRow>
                <DataRow title="卖出说明">
                    <Form.Item name={'sellDescription'}>
                        <TextArea rows={4} className="data-desc" />
                    </Form.Item>
                </DataRow>
            </Form>
        </PageContainer>
    );
};

export default HoldSell;
